import axios from "axios";
import { getEnvironment } from "../utils/getEnvironment";

const environment = getEnvironment();

interface GoogleResponse {
    token: string;
    id: string;
    email: string;
    verified_email: boolean;
    name: string;
    given_name: string;
    family_name: string;
    picture: string;
    locale: string;
}

interface UserRaw {
    key: string;
    created: Date;
    signin: Date;
    name: string;
    birth: string;
    phone: string;
    instagram: string;
    email: string;
    status: string;
    note: string;
    total: number;
    image?: string | null;
    rkey?: string;
    appInstalled?: boolean;
}

export default class User {
    key: string;
    created: Date;
    signin: Date;
    name: string;
    birth: string;
    phone: string;
    instagram: string;
    email: string;
    status: string;
    note: string;
    total: number;
    image?: string | null;
    rkey?: string;
    appInstalled?: boolean;

    constructor(data: UserRaw) {
        this.key = data.key;
        this.created = data.created;
        this.signin = data.signin;
        this.name = data.name;
        this.birth = data.birth;
        this.phone = data.phone;
        this.instagram = data.instagram;
        this.email = data.email;
        this.status = data.status;
        this.note = data.note;
        this.total = data.total;
        this.image = data.image;
        this.rkey = data.rkey;
        this.appInstalled = data.appInstalled;
    }

    public static readonly register = async (
        uuid: string,
        brand: string,
        email: string,
        name: string,
        phone: string,
        instagram: string,
        status: string,
        note: string,
        birth?: string,
        image?: string | null
    ) => {
        return (
            await axios
                .post(
                    environment.api_url + "/" + brand + "/user",
                    {
                        email,
                        name,
                        birth,
                        phone,
                        instagram,
                        status,
                        note,
                        image,
                    },
                    {
                        headers: {
                            "User-Token": uuid,
                        },
                    }
                )
                .then((res) => (res.status === 200 ? res.data : undefined))
                .catch(console.error)
        ).key as string | undefined;
    };

    public static readonly updateStatus = async (
        uuid: string,
        brand: string,
        user: string,
        status: string
    ) =>
        axios.put(
            environment.api_url + "/" + brand + "/user/" + user,
            {
                status,
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );

    public static readonly updateDetails = async (
        uuid: string,
        brand: string,
        user: string,
        phone: string,
        instagram: string,
        birthday?: string
    ) =>
        axios.put(
            environment.api_url + "/" + brand + "/user/" + user,
            {
                birth: birthday,
                phone,
                instagram,
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );

    public static readonly updateNote = async (
        uuid: string,
        brand: string,
        user: string,
        note: string
    ) =>
        axios.put(
            environment.api_url + "/" + brand + "/user/" + user,
            {
                note,
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );

    public static readonly list = async (uuid: string, brand: string) =>
        (
            (await axios
                .get(environment.api_url + "/" + brand + "/user/list", {
                    headers: {
                        "User-Token": uuid,
                    },
                })
                .then((res) => (res.status === 200 ? res.data : undefined))
                .catch(console.error)) as UserRaw[] | undefined
        )?.map((x) => new User(x));

    public static readonly listEx = async (uuid: string, brand: string) =>
        (
            (await axios
                .get(environment.api_url + "/" + brand + "/user/listex", {
                    headers: {
                        "User-Token": uuid,
                    },
                })
                .then((res) => (res.status === 200 ? res.data : undefined))
                .catch(console.error)) as UserRaw[] | undefined
        )?.map((x) => new User(x));

    public static readonly info = async (
        uuid: string,
        brand: string,
        user: string
    ) => {
        const data = (await axios
            .get(environment.api_url + "/" + brand + "/user/" + user, {
                headers: {
                    "User-Token": uuid,
                },
            })
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as UserRaw | undefined;
        if (data) return new User(data);
        return undefined;
    };

    public static readonly googleSignIn = async (
        brand: string,
        token: string
    ) => {
        const data = await axios
            .get(
                `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${token}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                }
            )
            .then((res) => res.data as GoogleResponse)
            .catch((err) => console.error(err));
        if (!data) return;
        return (
            await axios
                .put(environment.api_url + "/" + brand + "/signin", {
                    email: data.email,
                    token,
                })
                .then((res) => (res.status === 200 ? res.data : undefined))
                .catch(console.error)
        )?.key as string | undefined;
    };

    public static readonly googleSignUp = async (
        brand: string,
        token: string
    ) => {
        const data = await axios
            .get(
                `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${token}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                }
            )
            .then((res) => res.data as GoogleResponse)
            .catch((err) => console.error(err));
        if (!data) return;
        return (
            await axios
                .post(environment.api_url + "/" + brand + "/signup", {
                    email: data.email,
                    name: data.given_name + data.family_name,
                    token,
                })
                .then((res) => (res.status === 200 ? res.data : undefined))
                .catch(console.error)
        )?.key as string | undefined;
    };

    public static readonly getStatuses = async (
        uuid: string,
        brand: string
    ): Promise<string[]> => {
        try {
            const res = await axios.get(
                `${environment.api_url}/${brand}/user/statuses`,
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            );
            return res.data;
        } catch (err) {
            console.error("Failed to fetch user statuses", err);
            return [];
        }
    };
}

export class Analytics {
    total: number;
    average: number;
    projectedApp: number;
    projectedPro: number;
    count: number;
    active: number;
    cancelled: number;
    noshows: number;
    signup: Date;

    constructor(
        total: number,
        average: number,
        projectedApp: number,
        projectedPro: number,
        count: number,
        active: number,
        cancelled: number,
        noshows: number,
        signup: Date
    ) {
        this.total = total;
        this.average = average;
        this.projectedApp = projectedApp;
        this.projectedPro = projectedPro;
        this.count = count;
        this.active = active;
        this.cancelled = cancelled;
        this.noshows = noshows;
        this.signup = signup;
    }

    public static readonly info = async (
        uuid: string,
        brand: string,
        user: string
    ) =>
        (await axios
            .get(
                environment.api_url +
                    "/" +
                    brand +
                    "/user/" +
                    user +
                    "/analytics",
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as Analytics | undefined;
}
